import React, { useEffect, useState } from 'react';
import type { UserProps } from '../dashboard/types';
import { getUsers, loginAsUser } from '@src/Api';
import { Card, Table, Container, Spinner, Modal } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { deleteAccount } from '@src/Api';
import 'bootstrap/dist/css/bootstrap.min.css';
import { CustomToast } from '@src/components/toast';
import EditUserModal from './userModal';

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserProps[]>([]);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastError, setToastError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserProps | null>(null);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [isAdminUser, setIsAdminUser] = useState<boolean>(false);

  useEffect(() => {
    isAdmin();
    setDataLoading(true);
    getAllUsers();
  }, []);

  const getAllUsers = async () => {
    try {
      const users = await getUsers();
      if (users) {
        setUsers(users.data.result);
      }
    } catch (error) {
      console.error('Failed to get users.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleAddCookie = async (user: UserProps | undefined) => {
    if (user === undefined) {
      setToastError('User is undefined');
      setShowToast(true);
      return;
    }

    const token = localStorage.getItem('jwt') || '';
    localStorage.setItem('adminJwt', token);

    const response = await loginAsUser(user.users_id, token);

    if (response && response.data) {
      const newToken = response.data.newToken;

      if (newToken) {
        try {
          localStorage.setItem('jwt', newToken);
          location.reload();
        } catch (error) {
          console.log('Failed to set token in localStorage:', error);
        }
      }
    }
  };

  const isAdmin = async () => {
    const jwt = localStorage.getItem('jwt');
    const adminJwt = localStorage.getItem('adminJwt');

    if (jwt && adminJwt) {
      if (jwt !== adminJwt) {
        setIsAdminUser(true);
      } else {
        setIsAdminUser(false);
      }
    } else {
      setIsAdminUser(false);
    }
  };

  const handleDeleteUser = (userId: number) => {
    setUserToDelete(userId);
    setShowModal(true);
  };

  const handleOpenModal = (user: UserProps) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const confirmDeleteUser = async () => {
    if (userToDelete === null) return;

    setLoading(true);
    try {
      const response = await deleteAccount(userToDelete);
      if (response?.status === 200) {
        setShowToast(true);
        setUsers(users.filter((user) => user.users_id !== userToDelete));
        setUserToDelete(null);
      } else if (response?.data.error) {
        setToastError(response.data.error);
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
    } finally {
      setShowModal(false);
      setLoading(false);
    }
  };

  return (
    <Card className="mt-4">
      <Card.Header>All Users</Card.Header>
      <Card.Body className="custom-card-body">
        <Modal className="modal-placement" show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to delete this user?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            {loading ? <Spinner className="custom-spinner-small-modal"></Spinner> : <Button onClick={confirmDeleteUser}>Yes, Delete</Button>}
          </Modal.Footer>
        </Modal>
        <EditUserModal showModal={showUserModal} handleModal={() => setShowUserModal(false)} selectedUser={selectedUser} refreshUsers={getAllUsers} />
        <Table className="enhanced-table" striped responsive>
          <thead>
            <tr>
              <th>User Id</th>
              <th>Email</th>
              <th>Company</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          {dataLoading ? (
            <tbody>
              <tr>
                <td colSpan={5} className="table-loader">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {users.map((user) => (
                <tr key={user.users_id}>
                  <td>{user.users_id}</td>
                  <td>{user.users_email}</td>
                  <td>{user.users_company_name}</td>
                  <td>{user.users_role}</td>
                  <td>
                    <Button disabled={isAdminUser} className="table-button" onClick={() => handleAddCookie(user)}>
                      Login
                    </Button>
                    <Button className="table-button" onClick={() => handleOpenModal(user)}>
                      Edit
                    </Button>
                    <Button onClick={() => handleDeleteUser(user.users_id)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </Table>
        <Container className="toast-b-right">
          <CustomToast
            show={showToast}
            title={toastError ? 'Error' : 'User deleted'}
            message={toastError ? toastError : 'User successfully deleted from the database.'}
            onClose={() => setShowToast(false)}
            duration={5000}
            type={toastError ? 'error' : 'success'}
          />
        </Container>
      </Card.Body>
    </Card>
  );
};
